import {
	OrderAction,
	PaymentRowTone,
	useGetInvoicePdfUrlLazyQuery,
	useGetOrderTransactionsQuery,
	useMarkOrderProductInvoicePaidMutation,
	useMarkOrderSampleInvoicePaidMutation,
	type GetOrderQuery,
} from '@codezo/graphql';
import { OrderTransactions, type OrderTransaction } from '@codezo/order-detail';
import { Banknote, Check, CreditCard, FileText } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../../components/ui/tooltip';
import { CurrencyText } from '../../../components/CurrencyText';
import { FilePreview, type FilePreviewItem } from '../../../components/ui/file-preview';
import { formatDate } from '../../../lib/utils';
import { useCurrencyStore } from '../../../stores/currency';
import type { Tone } from '@codezo/order-detail';
import { RecordBankTransferModal } from './RecordBankTransferModal';

type Order = NonNullable<GetOrderQuery['getOrder']>;

/**
 * `PaymentRowTone` says what a status MEANS; these are the colours for it.
 *
 * An enum, so this is exhaustive and a new tone breaks the build rather than
 * rendering grey. The status STRING is no longer read at all — it spans two
 * lifecycles and will gain values, which is exactly why the backend added a tone
 * to branch on and a `statusLabel` to print.
 */
const TONE: Record<PaymentRowTone, Tone> = {
	[PaymentRowTone.Settled]: 'success',
	[PaymentRowTone.Pending]: 'info',
	[PaymentRowTone.Awaiting]: 'warn',
	[PaymentRowTone.Ended]: 'danger',
	[PaymentRowTone.Neutral]: 'neutral',
};

/**
 * The order's payment history.
 *
 * Its own query rather than a field on the order, and its own refetch: marking
 * an invoice paid changes both this list and the order's available actions, so
 * the page refetches the order and this section refetches itself.
 */
export function OrderTransactionsSection({
	order,
	onOrderChanged,
}: {
	order: Order;
	/** Refetch the order — its actions change when an invoice is settled. */
	onOrderChanged: () => void;
}) {
	const { selectedCurrency } = useCurrencyStore();
	const { data, loading, refetch } = useGetOrderTransactionsQuery({
		variables: { orderId: order.id },
		fetchPolicy: 'no-cache',
	});

	const [fetchPdfUrl] = useGetInvoicePdfUrlLazyQuery();
	const [openingInvoice, setOpeningInvoice] = useState<number | null>(null);
	const [invoicePreview, setInvoicePreview] = useState<FilePreviewItem | null>(null);

	const [markProductInvoicePaid] = useMarkOrderProductInvoicePaidMutation();
	const [markSampleInvoicePaid] = useMarkOrderSampleInvoicePaidMutation();
	const [markingPaidId, setMarkingPaidId] = useState<string | null>(null);
	const [recordingTransfer, setRecordingTransfer] = useState(false);

	/**
	 * Which rows the server currently offers a manual settle for, and which kind
	 * of invoice each one is.
	 *
	 * Two keys, kept apart rather than merged: production and samples settle
	 * different invoices through different mutations, and a transaction id alone
	 * doesn't say which it is — which is exactly why samples were given their own
	 * action key instead of a second MARK_INVOICE_PAID.
	 *
	 * BOTH can be present at once. An order whose products and samples were
	 * invoiced separately has two outstanding invoices, and the sample action is
	 * emitted even while a production one is outstanding — so this is a map over
	 * the rows, never a single "the settle action".
	 *
	 * The rule for when an action appears is the backend's. This is only the
	 * lookup that puts the button on the row it names rather than in a detached
	 * toolbar, which could not say which invoice it meant.
	 */
	const settleActions = useMemo(() => {
		const byTransaction = new Map<string, OrderAction>();
		for (const a of order.availableActions ?? []) {
			if (!a.paymentTransactionId) continue;
			if (
				a.action === OrderAction.MarkInvoicePaid ||
				a.action === OrderAction.MarkSampleInvoicePaid
			) {
				byTransaction.set(a.paymentTransactionId, a.action);
			}
		}
		return byTransaction;
	}, [order.availableActions]);

	const openInvoice = async (invoiceNumber: number) => {
		setOpeningInvoice(invoiceNumber);
		try {
			const { data: pdf } = await fetchPdfUrl({
				variables: { invoiceNumber },
				fetchPolicy: 'no-cache',
			});
			const url = pdf?.getInvoicePdfUrl;
			if (!url) {
				toast.error('Invoice PDF is not available yet');
				return;
			}
			setInvoicePreview({
				id: `invoice-${invoiceNumber}`,
				url,
				name: `Invoice #${invoiceNumber}.pdf`,
				mimeType: 'application/pdf',
			});
		} catch {
			toast.error('Failed to open invoice');
		} finally {
			setOpeningInvoice(null);
		}
	};

	/**
	 * Settle one invoice by hand, through whichever mutation the action that put
	 * the button there belongs to.
	 *
	 * The message is the server's, printed as it comes: it reports success when
	 * there was nothing left to settle — the hourly reconciler got there first, or
	 * someone clicked twice — and names how many samples cleared when one sample
	 * invoice covered several. A generic string would flatten a settle that did
	 * work and a no-op into the same sentence, and both are successes.
	 */
	const handleMarkPaid = async (paymentTransactionId: string, action: OrderAction) => {
		setMarkingPaidId(paymentTransactionId);
		try {
			if (action === OrderAction.MarkSampleInvoicePaid) {
				const { data: result } = await markSampleInvoicePaid({
					variables: { paymentTransactionId },
				});
				if (!result?.markOrderSampleInvoicePaid?.success) {
					toast.error(
						result?.markOrderSampleInvoicePaid?.message || 'Failed to mark invoice paid'
					);
					return;
				}
				toast.success(result.markOrderSampleInvoicePaid.message || 'Invoice marked paid');
			} else {
				const { data: result } = await markProductInvoicePaid({
					variables: { paymentTransactionId },
				});
				if (!result?.markOrderProductInvoicePaid?.success) {
					toast.error(
						result?.markOrderProductInvoicePaid?.message ||
							'Failed to mark invoice paid'
					);
					return;
				}
				toast.success(result.markOrderProductInvoicePaid.message || 'Invoice marked paid');
			}

			// Both, and in this order: the list is what the admin is looking at,
			// and the order's actions are what decide whether the button is still
			// there to be clicked again.
			refetch();
			onOrderChanged();
		} catch {
			toast.error('Failed to mark invoice paid');
		} finally {
			setMarkingPaidId(null);
		}
	};

	// Superseded attempts — cancelled and failed invoices the API flags as
	// history — are dropped here. This section answers "what happened to the
	// money", and a failed attempt that was replaced never moved any.
	const rows = (data?.getOrderTransactionsV2 ?? []).filter((t) => !t.isHistory);

	const transactions: OrderTransaction[] = rows.map((txn) => {
		// Which settle, if any, this row is offered — the action's own key, so a
		// sample row goes to the sample mutation and a production row to the
		// production one.
		const settleAction = txn.paymentTransactionId
			? settleActions.get(txn.paymentTransactionId)
			: undefined;

		return {
			key: txn.key,
			title: `${txn.kind.charAt(0).toUpperCase()}${txn.kind.slice(1).toLowerCase()} payment`,
			status: { label: txn.statusLabel, tone: TONE[txn.tone] },
			icon: txn.method === 'Stripe' ? <CreditCard size={14} /> : <FileText size={14} />,
			meta: [
				txn.method,
				txn.invoiceNumber != null ? (
					<button
						type="button"
						onClick={() => openInvoice(txn.invoiceNumber!)}
						disabled={openingInvoice === txn.invoiceNumber}
						className="inline-flex cursor-pointer items-center gap-1 bg-transparent text-text-3 underline-offset-2 transition-colors hover:text-text-1 hover:underline disabled:opacity-60"
					>
						Invoice #{txn.invoiceNumber}
						<FileText size={10} />
					</button>
				) : null,
				formatDate(txn.date),
			].filter(Boolean),
			notes: txn.descriptions.map((desc) =>
				desc.sampleRound != null
					? `Sample round ${desc.sampleRound}: ${desc.text}`
					: desc.text
			),
			// The row's own currency, not the page's selector.
			amount: <CurrencyText amount={txn.totalPrice} currency={txn.currency} />,
			action: settleAction ? (
				<Button
					size="sm"
					variant="outline"
					onClick={() => handleMarkPaid(txn.paymentTransactionId!, settleAction)}
					disabled={markingPaidId === txn.paymentTransactionId}
				>
					<Check size={12} data-icon="inline-start" />
					{markingPaidId === txn.paymentTransactionId ? 'Marking…' : 'Mark invoice paid'}
				</Button>
			) : undefined,
		};
	});

	/* The order's totals (Paid, Remaining) follow the header currency; these rows
	   keep the currency each payment was made in. When the two differ, say so —
	   otherwise €1,000 here beside 7,460 kr. "Paid" reads as a mismatch. */
	const otherCurrency = rows.some((t) => t.currency !== selectedCurrency);

	return (
		<>
			<OrderTransactions
				transactions={transactions}
				loading={loading}
				note={
					otherCurrency
						? `Each amount is in the currency it was settled in. The order's totals are shown in ${selectedCurrency}.`
						: undefined
				}
				emptyState="No payments yet — they appear here as invoices are raised and settled."
				action={
					// For money that arrived without going through the platform. Needs a
					// client: the invoice it books is addressed to one — so without one the
					// button stays visible but disabled, saying why, rather than vanishing.
					order.organizationId ? (
						<Button
							size="sm"
							variant="outline"
							onClick={() => setRecordingTransfer(true)}
						>
							<Banknote size={12} data-icon="inline-start" />
							Record bank transfer
						</Button>
					) : (
						<Tooltip>
							<TooltipTrigger render={<span className="flex w-fit" tabIndex={0} />}>
								<Button size="sm" variant="outline" disabled>
									<Banknote size={12} data-icon="inline-start" />
									Record bank transfer
								</Button>
							</TooltipTrigger>
							<TooltipContent>
								Attach a client to this order first — the invoice is booked to them.
							</TooltipContent>
						</Tooltip>
					)
				}
			/>
			<RecordBankTransferModal
				open={recordingTransfer}
				onOpenChange={setRecordingTransfer}
				orderId={order.id}
				onRecorded={() => {
					refetch();
					onOrderChanged();
				}}
			/>
			{invoicePreview && (
				<FilePreview files={[invoicePreview]} onClose={() => setInvoicePreview(null)} />
			)}
		</>
	);
}
